import {useEffect, useRef, useState} from "react";
import {FileText, FolderOpen, LayoutDashboard, Play} from "lucide-react";
import useMyCase from "../../myCase/useMyCase";
import OrbitLoader from "../../../ui/components/OrbitLoader";
import VideoModal from "../../video/components/VideoModal";
import VideoPreview from "../../video/components/VideoPreview";
import {toHoursMinutes} from "../../video/components/VideoStats";
import ensureYouTubeApi from "../../video/utils/ensureYouTubeApi";
import {
  DEFAULT_LANGUAGE,
  getPreferredLanguage,
} from "../../video/utils/videoLanguage";
import {FLAGS} from "../../featureFlags/flags";
import {useFeatureEnabled} from "../../featureFlags/useFeatureFlag";
import getWatchedProgress from "../../video/utils/getWatchedProgress";
import {isCompleted} from "../../video/utils/getProgressData";
import useActivity from "../../video/hooks/useActivity";
import useWatchHistory from "../../video/hooks/useWatchHistory";
import {useStorage} from "../../video/hooks/useStorage";
import {useSync} from "../../video/hooks/useSync";
import {
  displayedOrientationFamilies,
  snapOrientationVideo,
  tanfOrientationVideo,
} from "../orientationRules";

// The app's front door, v2 (Marco 08-25). Top to bottom:
//   1. "Start here": ONLY the orientation video(s) this person still owes,
//      per program family + transition bucket + orientation_complete
//      (rules in ../orientationRules.js). The required copy promises only
//      what is true by construction: completion reporting is automatic.
//   2. Confirmed hours this month, in the same visual language as the video
//      dashboard's "Your progress this month" widget (hours_this_month).
//   3. Quick links -- only after orientation is done (or not owed). While it
//      is still required, Home is the only usable page; App also locks the
//      sidebar and permalinks so the rest of the platform cannot be skipped
//      to from elsewhere.
// Orientation videos play IN PLACE (Marco 08-25): the same VideoModal the
// dashboard uses, mounted here with the same useStorage/useSync wiring, so
// closing it leaves you on home and watch credit flows exactly as on the
// dashboard. Its queue and recommendations carry ONLY the other owed
// orientation video (recentVideos=[]), never the general catalog. The
// dashboard no longer lists orientation in its own browse sections; the
// homepage is where the requirement lives now.
const DHW_BLUE = "#001489";

const QuickLink = ({icon: Icon, title, text, onClick}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-4 rounded-[2rem] border border-[#ECECEC] bg-white p-5 text-left shadow-sm transition-all hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-offset-2 md:p-6"
  >
    <div
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white"
      style={{backgroundColor: DHW_BLUE}}
      aria-hidden="true"
    >
      <Icon size={22} />
    </div>
    <div>
      <h3 className="text-base font-bold text-[#191919]">{title}</h3>
      <p className="mt-0.5 text-sm text-slate-600">{text}</p>
    </div>
  </button>
);

// Mirrors VideoStats' widget styling so "hours" reads the same everywhere.
const HoursTicker = ({hours}) => {
  const {hours: h, minutes: m} = toHoursMinutes((hours?.hours ?? 0) * 3600);
  const parts = [];
  if (h > 0) parts.push({value: h, unit: h === 1 ? "hour" : "hours"});
  if (m > 0 || h === 0)
    parts.push({value: m, unit: m === 1 ? "minute" : "minutes"});
  return (
    <div className="mb-6 rounded-2xl border border-slate-100 bg-[#F8FAFC] p-4 shadow-sm">
      <p className="text-sm font-semibold text-[#262626] tracking-tight">
        Your hours this month
      </p>
      {hours ? (
        <>
          <p className="mt-1 leading-none">
            {parts.map(({value, unit}, i) => (
              <span key={unit}>
                {i > 0 && " "}
                <span className="text-3xl font-bold text-[#001489]">
                  {value}
                </span>{" "}
                <span className="text-lg font-bold text-[#001489]">{unit}</span>
              </span>
            ))}
          </p>
          <p className="mt-1 text-xs font-medium text-[#262626]/60">
            confirmed so far this month
          </p>
        </>
      ) : (
        <p className="mt-1 text-sm text-slate-600">
          No hours are on file yet this month. Hours show up here after your
          weekly check-in with your case manager.
        </p>
      )}
    </div>
  );
};

const OrientationCard = ({video, programLabel, completed, onOpenVideo}) => (
  <button
    type="button"
    onClick={onOpenVideo}
    aria-label={`Watch the ${programLabel} orientation video`}
    className="w-full overflow-hidden rounded-[2rem] border border-[#ECECEC] bg-white text-left shadow-sm transition-all hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:flex sm:items-stretch"
  >
    {/* VideoPreview positions itself absolute inset-0, so this wrapper MUST be
        relative or the resolved thumbnail anchors to the viewport and paints
        over the whole page (the "full-screen thumbnail" bug). */}
    <div className="relative aspect-video w-full overflow-hidden bg-slate-100 sm:w-64 sm:shrink-0">
      <VideoPreview videoUrl={video.url} />
    </div>
    <div className="flex items-center gap-3 p-4 md:p-5">
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white"
        style={{backgroundColor: DHW_BLUE}}
        aria-hidden="true"
      >
        <Play size={20} />
      </div>
      <div>
        <span
          className="rounded-full px-2.5 py-0.5 text-xs font-black uppercase tracking-wide text-white"
          style={{backgroundColor: completed ? "#007864" : "#B3261E"}}
        >
          {completed ? "Completed" : "Required"}
        </span>
        <h4 className="mt-1 text-base font-bold text-[#191919]">
          {programLabel} orientation video
        </h4>
        <p className="mt-0.5 text-sm text-slate-600">
          {video.title || video.video_title || ""}
        </p>
      </div>
    </div>
  </button>
);

const historyHasEntries = (history) =>
  Boolean(history) && Object.keys(history).length > 0;

const HomePage = ({
  videos,
  showPortal,
  loggedInUser,
  onGo,
  onWatchHistoryChange,
  videosReady = true,
  initialWatchHistory = null,
}) => {
  const {status, caseData} = useMyCase(loggedInUser);
  const referrals = caseData?.referrals ?? [];
  // Not owedOrientationFamilies directly: a participant in both families is
  // shown the TANF video only, matching the dashboard catalog. See
  // displayedOrientationFamilies for the compliance consequence.
  const owed =
    status === "success" ? displayedOrientationFamilies(referrals) : [];

  // Same persistence + sync stack as the dashboard (one instance per mounted
  // view; home and dashboard never mount together, so the sessionStorage key
  // has a single writer at any time).
  const user = loggedInUser;
  const {activity, watchHistory, setActivity, setWatchHistory} = useStorage({
    user,
  });
  const {activityRef} = useActivity({user, activity, setActivity});
  const {historyRef, historyReady} = useWatchHistory({
    user,
    watchHistory,
    setWatchHistory,
  });
  const {handleVideoProgress} = useSync({
    user,
    activity,
    activityRef,
    setActivity,
    watchHistory,
    historyRef,
    setWatchHistory,
  });
  useEffect(() => {
    ensureYouTubeApi();
  }, []);

  // useStorage starts as {} until sessionStorage restore + the remote merge.
  // App already has the login snapshot (and any live history from a previous
  // view); use that for completion until hydration settles. After that, an
  // authoritative empty {} must replace the snapshot (wiped Dynamo / a
  // different DOB-matched participant) so stale progress cannot unlock Home.
  const historyForProgress = historyReady
    ? watchHistory
    : historyHasEntries(watchHistory)
      ? watchHistory
      : (initialWatchHistory ?? {});

  // Orientation plays here, not just on the dashboard, so the position has to
  // reach App too — the sign-out flush reads it from there. Do not publish
  // the cold {} before hydration or App will treat it as "no progress".
  useEffect(() => {
    if (!onWatchHistoryChange || !historyReady) return;
    onWatchHistoryChange(watchHistory);
  }, [watchHistory, onWatchHistoryChange, historyReady]);
  const [playing, setPlaying] = useState(null);
  // Read once on mount: the catalog toggle is on another page, so it cannot
  // change underneath this one while a video is open here.
  const [storedLanguage] = useState(getPreferredLanguage);
  // Same flag as the catalog toggle: with the feature off, the stored
  // preference is stale state from a demo and must not leak into the player
  // here either. English is the default.
  const {enabled: languageToggleEnabled} = useFeatureEnabled(
    FLAGS.VIDEOS_LANGUAGE,
    {userId: user?.customer_id},
  );
  const homeLanguage = languageToggleEnabled
    ? storedLanguage
    : DEFAULT_LANGUAGE;

  // Completion, from the same watch-history rule as the player's badge
  // (80% for orientation, matching both CRM writers).
  const completedNow = (video) =>
    isCompleted(getWatchedProgress(historyForProgress[video.id]), video);
  const sessionCompletedRef = useRef(new Set());
  // Capture an in-session completion whenever the current video stops being
  // the one playing: close AND auto-advance/switch both route through here,
  // or a video finished right before switching would vanish instead of
  // wearing its Completed tag.
  const leaveCurrent = () => {
    if (playing && completedNow(playing)) {
      sessionCompletedRef.current.add(playing.id);
    }
  };
  const closePlayer = () => {
    leaveCurrent();
    setPlaying(null);
  };
  const switchVideo = (next) => {
    leaveCurrent();
    setPlaying(next);
  };
  // Cards finished BEFORE this visit disappear; a card finished DURING this
  // visit stays, wearing a Completed tag, until the next page load. The CRM
  // orientation_complete flag catches up via the sync pipeline and clears
  // the family server-side afterwards.
  const orientationCards = owed
    .map((family) => ({
      family,
      video:
        family === "TANF"
          ? tanfOrientationVideo(videos)
          : snapOrientationVideo(videos),
    }))
    .filter((c) => c.video)
    .map((c) => ({...c, completed: completedNow(c.video)}))
    .filter(
      (c) =>
        !c.completed ||
        sessionCompletedRef.current.has(c.video.id) ||
        c.video.id === playing?.id,
    );
  const requiredLeft = orientationCards.filter((c) => !c.completed).length;
  // Same lock App uses for sidebar/permalinks: no skip path from Home while
  // we still don't know, or while a required video is unfinished.
  const platformLocked =
    status === "loading" || !videosReady || requiredLeft > 0;
  // "Up next" offers only the OTHER owed orientation video, nothing else,
  // and never one that is already completed. The currently-playing video
  // LEADS the queue so VideoModal can find it (it locates the current video
  // and auto-advances to whatever follows); without it idxInQueue is -1 and
  // nothing advances.
  const orientationQueue = playing
    ? [
        playing,
        ...orientationCards
          .filter((c) => !c.completed && c.video.id !== playing.id)
          .map((c) => c.video),
      ]
    : [];
  return (
    <div data-testid="home" className="min-h-full bg-white pb-10 font-sans">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#191919]">Welcome</h2>
        <p className="mt-1 text-base text-slate-600">
          This is your home page for Idaho Employment and Training.
        </p>
      </div>

      {playing && (
        <VideoModal
          video={playing}
          // The home page has no language toggle of its own, so the player
          // sheet follows the preference the learner set on the catalog --
          // otherwise picking Espanol there and opening an orientation video
          // here would drop back to English mid-flow.
          language={homeLanguage}
          queue={orientationQueue}
          watchHistory={historyForProgress}
          onClose={closePlayer}
          recentVideos={[]}
          onProgress={handleVideoProgress}
          onPlay={() => {}}
          onSwitchVideo={switchVideo}
        />
      )}

      {/* While the case is loading we know neither the hours nor whether
          orientation is owed; a spinner beats a blank-state that reads as a
          claim ("no hours") and beats cards popping in late. */}
      {status === "loading" && (
        <div
          data-testid="home-loading"
          className="mb-6 flex justify-center py-10"
        >
          <OrbitLoader />
        </div>
      )}

      {status !== "loading" && orientationCards.length > 0 && (
        <section className="mb-6">
          <h3 className="text-lg font-bold text-[#191919]">Start here</h3>
          <p className="mt-1 mb-3 text-sm text-slate-600">
            {requiredLeft === 0
              ? "Nice work, you finished. Your case manager was notified automatically."
              : requiredLeft === 1
                ? "This video is required before you can use the rest of the platform. Your case manager is notified automatically when you finish it."
                : "These videos are required before you can use the rest of the platform. Your case manager is notified automatically when you finish each one."}
          </p>
          <div className="space-y-4">
            {orientationCards.map(({family, video, completed}) => (
              <OrientationCard
                key={family}
                video={video}
                programLabel={family}
                completed={completed}
                onOpenVideo={() => setPlaying(video)}
              />
            ))}
          </div>
        </section>
      )}

      {status !== "loading" && (
        <HoursTicker hours={caseData?.hoursThisMonth ?? null} />
      )}

      {!platformLocked && (
        <div className="grid gap-4 md:grid-cols-2">
          {showPortal && (
            <QuickLink
              icon={FolderOpen}
              title="My Work Plan"
              text="See the plan you and your case manager made."
              onClick={() => onGo("mycase")}
            />
          )}
          <QuickLink
            icon={LayoutDashboard}
            title="Training Videos"
            text="Watch videos and build your job skills."
            onClick={() => onGo("dashboard")}
          />
          {showPortal && (
            <QuickLink
              icon={FileText}
              title="My Documents"
              text="See the documents on your case."
              onClick={() => onGo("documents")}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default HomePage;
